import React, { useState, useEffect } from "react";

const InstallPrompt = () => {
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [hover, setHover] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const isInstalled =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone ||
      document.referrer.includes("android-app://");

    if (isInstalled) return; // already installed, never show prompt

    if (window.__deferredInstallPrompt) {
      setPrompt(window.__deferredInstallPrompt);
      setShow(true);
      setTimeout(() => setVisible(true), 50);
    }

    const handler = (e) => {
      e.preventDefault();
      setPrompt(e);
      window.__deferredInstallPrompt = e;
      setShow(true);
      setTimeout(() => setVisible(true), 50);
    };

    // ✅ If installed via browser's native banner, hide our prompt too
    const onInstalled = () => {
      window.__deferredInstallPrompt = null;
      handleClose();
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice;
    handleClose();
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => setShow(false), 350);
  };

  if (!show) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@1,700&family=DM+Sans:wght@400;600;700;800&display=swap');
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.55); opacity: 0; }
        }
      `}</style>

      <div
        style={{
          position: "fixed",
          bottom: "1.75rem",
          left: "50%",
          transform: `translateX(-50%) translateY(${visible ? "0" : "120px"})`,
          opacity: visible ? 1 : 0,
          transition:
            "transform 0.4s cubic-bezier(.22,1,.36,1), opacity 0.35s ease",
          zIndex: 10000,
          width: "min(92vw, 380px)",
        }}
      >
        {/* Card */}
        <div
          style={{
            background: "#fff",
            border: "1.5px solid #ede9fe",
            borderRadius: "20px",
            boxShadow:
              "0 24px 64px rgba(102,126,234,0.18), 0 4px 16px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          {/* Top gradient strip */}
          <div
            style={{
              height: "3px",
              background: "linear-gradient(90deg, #667eea, #764ba2)",
            }}
          />

          <div style={{ padding: "1.5rem 1.5rem 1.75rem" }}>
            {/* Close button */}
            <button
              onClick={handleClose}
              style={{
                position: "absolute",
                top: "1.1rem",
                right: "1.1rem",
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "rgba(102,126,234,0.08)",
                border: "1px solid #ede9fe",
                color: "#9ca3af",
                fontSize: "1.1rem",
                lineHeight: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(102,126,234,0.15)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "rgba(102,126,234,0.08)")
              }
            >
              ×
            </button>

            {/* Icon + heading row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                marginBottom: "1rem",
              }}
            >
              {/* Pulsing icon */}
              <div style={{ position: "relative", flexShrink: 0 }}>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: "rgba(102,126,234,0.2)",
                    animation: "pulse-ring 1.8s ease-out infinite",
                  }}
                />
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                    <path d="M9 10l3 3 3-3" />
                    <line x1="12" y1="7" x2="12" y2="13" />
                    <line x1="12" y1="18" x2="12" y2="18.01" />
                  </svg>
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontSize: "0.68rem",
                    fontWeight: "700",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "#7c3aed",
                    marginBottom: "0.2rem",
                  }}
                >
                  Free Install
                </div>
                <h3
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "1.15rem",
                    fontWeight: "700",
                    fontStyle: "italic",
                    color: "#1e1b4b",
                    lineHeight: 1.2,
                    margin: 0,
                  }}
                >
                  Install NORYQ
                </h3>
              </div>
            </div>

            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.85rem",
                color: "#6b7280",
                lineHeight: 1.65,
                margin: "0 0 1.25rem",
              }}
            >
              Add NORYQ to your home screen for instant access — no app store
              needed.
            </p>

            {/* CTA Button */}
            <button
              onClick={install}
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              style={{
                width: "100%",
                padding: "0.9rem",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: "700",
                fontSize: "0.93rem",
                letterSpacing: "0.04em",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
                boxShadow: hover
                  ? "0 10px 28px rgba(118,75,162,0.38)"
                  : "0 4px 16px rgba(102,126,234,0.28)",
                transform: hover ? "translateY(-1px)" : "translateY(0)",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Install App
            </button>

            {/* Dismiss link */}
            <button
              onClick={handleClose}
              style={{
                display: "block",
                width: "100%",
                marginTop: "0.75rem",
                background: "none",
                border: "none",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.78rem",
                color: "#9ca3af",
                cursor: "pointer",
                textAlign: "center",
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#7c3aed")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InstallPrompt;
